<div class="flex flex-col gap-4 p-5 border rounded-md">
  <h2 class="font-semibold text-4">{{ translatePrefix + 'report-production' | translate }}</h2>

  <!-- FORM -->
  <form class="flex flex-col gap-5" [formGroup]="productionDetailsForm">
    <!-- OBJECTIVE SELECTION -->
    <div class="flex flex-col gap-2">
      <h3 class="text-2 font-medium uppercase">{{ translatePrefix + 'objective-selection' | translate }}</h3>
      <div class="grid grid-cols-3 gap-4">
        <!-- Commission owners -->
        <app-user-form-dropdown
          [multiple]="true"
          [label]="translatePrefix + 'commission-owner' | translate"
          class="!h-11"
          placeholder="Select"
          formControlName="commissionOwners"
          optionLabel="fullName"></app-user-form-dropdown>

        <!-- Commission agents -->
        <app-user-form-dropdown
          [multiple]="true"
          [label]="translatePrefix + 'commission-agent' | translate"
          class="!h-11"
          placeholder="Select"
          formControlName="commissionAgents"
          userType="permission"
          optionLabel="fullName"></app-user-form-dropdown>

        <!-- Commission team -->
        <app-form-control
          [control]="productionDetailsForm.get('commissionTeams')"
          [floatLabel]="true"
          [label]="translatePrefix + 'commission-team' | translate">
          <p-multiSelect
            input
            [filter]="false"
            [options]="commissionTeams"
            optionLabel="name"
            optionValue="id"
            formControlName="commissionTeams"
            dropdownIcon="icon-wl-arrow-drop-down">
            <ng-template let-value pTemplate="selectedItems">
              <span>{{ getCommissionTeamsValue(value) || (translatePrefix + 'all-groups' | translate) }}</span>
            </ng-template>
          </p-multiSelect>
        </app-form-control>
      </div>
    </div>

    <!-- FIELD SELECTION -->
    <div class="flex flex-col gap-2">
      <h3 class="text-2 font-medium uppercase">{{ translatePrefix + 'field-selection' | translate }}</h3>
      <div class="grid grid-cols-3 gap-4">
        <!-- Source - Sub source -->
        <app-form-control
          [control]="productionDetailsForm.get('sources')"
          [floatLabel]="true"
          [label]="translatePrefix + 'source-sub-source' | translate">
          <p-treeSelect
            input
            [filter]="false"
            inputId="id"
            [options]="sources"
            scrollHeight="360px"
            containerStyleClass="w-full"
            formControlName="sources"
            dropdownIcon="icon-wl-arrow-drop-down"
            selectionMode="checkbox">
            <ng-template pTemplate="triggericon">
              <i class="icon-wl-arrow-drop-down text-6 text-neutral-1000 mr-1.5"></i>
            </ng-template>

            <ng-template pTemplate="value">
              <span>
                {{ sourcesSelectedCount > 0 ? sourcesSelectedCount + ' items selected' : ('common.all' | translate) }}
              </span>
            </ng-template>

            <ng-template pTemplate="itemtogglericon" let-implicit>
              <i
                class="text-6 text-neutral-1000"
                [ngClass]="implicit ? 'icon-wl-arrow-drop-down' : 'icon-wl-arrow-drop-right'"></i>
            </ng-template>
          </p-treeSelect>
        </app-form-control>

        <!-- Policy status -->
        <app-form-control
          [control]="productionDetailsForm.get('policyStatuses')"
          [floatLabel]="true"
          [label]="translatePrefix + 'policy-status-detail' | translate">
          <p-treeSelect
            input
            [filter]="false"
            [options]="policyStatuses"
            scrollHeight="360px"
            containerStyleClass="w-full"
            formControlName="policyStatuses"
            dropdownIcon="icon-wl-arrow-drop-down"
            selectionMode="checkbox">
            <ng-template pTemplate="triggericon">
              <i class="icon-wl-arrow-drop-down text-6 text-neutral-1000 mr-1.5"></i>
            </ng-template>

            <ng-template pTemplate="itemtogglericon" let-implicit>
              <i
                class="text-6 text-neutral-1000"
                [ngClass]="implicit ? 'icon-wl-arrow-drop-down' : 'icon-wl-arrow-drop-right'"></i>
            </ng-template>

            <ng-template let-value pTemplate="value">
              <span>{{ getTreeNodeValue(value) || (translatePrefix + 'all-statuses' | translate) }}</span>
            </ng-template>
          </p-treeSelect>
        </app-form-control>

        <!-- Company -->
        <app-form-control
          [control]="productionDetailsForm.get('companies')"
          [floatLabel]="true"
          [label]="translatePrefix + 'company' | translate">
          <p-multiSelect
            input
            [filter]="false"
            [options]="companies"
            optionLabel="name"
            optionValue="id"
            formControlName="companies"
            dropdownIcon="icon-wl-arrow-drop-down"
            (onChange)="onChangeCompany()">
            <ng-template let-value pTemplate="selectedItems">
              <span>{{ getCompaniesValue(value) || (translatePrefix + 'all-companies' | translate) }}</span>
            </ng-template>
          </p-multiSelect>
        </app-form-control>

        <!-- Product type -->
        <app-form-control
          [control]="productionDetailsForm.get('productTypes')"
          [floatLabel]="true"
          [label]="translatePrefix + 'product-type' | translate">
          <p-multiSelect
            input
            [filter]="false"
            [options]="productTypeOptions"
            optionLabel="name"
            optionValue="id"
            formControlName="productTypes"
            dropdownIcon="icon-wl-arrow-drop-down"
            (onChange)="onChangeProductType()">
            <ng-template let-value pTemplate="selectedItems">
              <span>{{ getProductTypesValue(value) || (translatePrefix + 'all-types' | translate) }}</span>
            </ng-template>
          </p-multiSelect>
        </app-form-control>

        <!-- Product name -->
        <app-form-control
          [control]="productionDetailsForm.get('productNames')"
          [floatLabel]="true"
          [label]="translatePrefix + 'product-name' | translate">
          <p-multiSelect
            input
            [filter]="false"
            [options]="productOptions"
            optionLabel="name"
            optionValue="id"
            formControlName="productNames"
            dropdownIcon="icon-wl-arrow-drop-down">
            <ng-template let-value pTemplate="selectedItems">
              <span>{{ getProductsValue(value) || (translatePrefix + 'all-products' | translate) }}</span>
            </ng-template>
          </p-multiSelect>
        </app-form-control>

        <!-- Group by -->
        <app-form-control
          [control]="productionDetailsForm.get('groupBy')"
          [floatLabel]="true"
          [label]="translatePrefix + 'group-by' | translate"
          [required]="true">
          <p-dropdown
            input
            [filter]="false"
            [options]="ProductDetailsGroupBys"
            styleClass="w-full h-12"
            optionLabel="title"
            optionValue="value"
            formControlName="groupBy"
            dropdownIcon="icon-wl-arrow-drop-down"></p-dropdown>
        </app-form-control>
      </div>
    </div>

    <!-- DATE -->
    <div class="flex flex-col gap-2">
      <h3 class="text-2 font-medium uppercase">{{ translatePrefix + 'date' | translate }}</h3>
      <div class="grid grid-cols-3 gap-4">
        <app-custom-calendar
          [isHasAllTime]="true"
          [title]="translatePrefix + 'created-date' | translate"
          [(datesSelected)]="createdDates"
          [isInput]="true"
          [required]="true"></app-custom-calendar>

        <app-custom-calendar
          [isHasAllTime]="true"
          [title]="translatePrefix + 'effective-date' | translate"
          [(datesSelected)]="effectiveDates"
          [isInput]="true"></app-custom-calendar>
      </div>
    </div>
  </form>
  <div class="flex flex-row">
    <app-button
      variant="outline-primary"
      [label]="exportSelectedFieldsText | translate"
      size="sm"
      (click)="isSelectionDialogVisible = true">
    </app-button>
  </div>

  <!-- ACTIONS -->
  <div class="flex items-center gap-2 justify-end">
    @if (productionDetailsForm.valid && createdDates.length === 2) {
      <app-button
        variant="ghost-primary"
        [label]="'action.reset' | translate"
        size="sm"
        (onClick)="onReset()"></app-button>
    }

    <app-button
      [label]="translatePrefix + 'run-report' | translate"
      size="sm"
      [disabled]="productionDetailsForm.invalid || createdDates.length < 2"
      [loading]="isRunning"
      (onClick)="onRunReport()"></app-button>
  </div>
</div>

<app-report-field-selection
  [(visible)]="isSelectionDialogVisible"
  [(data)]="exportSelectedFields"
  [requiredFields]="requiredExportFieldsProductionDetailsKey"
  [isAllFieldSelected]="isAllExportFieldsSelected"
  (isAllFieldSelectedChange)="toggleAllExportFields($event)">
</app-report-field-selection>
